import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import FirstVisitMonitorLink from "@/components/feature/FirstVisitMonitorLink";
import {
  ConcernList,
  FeatureList,
  TreatmentIndexNav,
  TreatmentSection,
} from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable, RiskList } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentCta, TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentPriceLink from "@/pages/treatment/components/TreatmentPriceLink";
import StemSupDripHero from "./StemSupDripHero";
import { isPendingValue } from "@/utils/isPendingContent";

/* NOTE: 本ページは広告LPではなく公式サイト。効果断定表現・未確認の医療情報は掲載しない。
   「幹細胞そのものを点滴する」「幹細胞移植」「細胞が蘇る」「若返る」「病気が治る」等の表現、
   未確定の由来・濃度・製造方法・成分量・治療回数・入手経路・製造元は一切補完しない。
   確定していない項目は「—」または「正式情報を確認のうえ掲載します」として後から追記できる構造にする。 */

const indexItems = [
  { label: "StemSup点滴とは", target: "sd-about" },
  { label: "StemSupとは", target: "sd-stemsup" },
  { label: "幹細胞そのものとの違い", target: "sd-difference" },
  { label: "このような方へ", target: "sd-concerns" },
  { label: "特徴", target: "sd-features" },
  { label: "施術情報", target: "sd-method" },
  { label: "施術の流れ", target: "sd-flow" },
  { label: "施術時間・ダウンタイム", target: "sd-downtime" },
  { label: "リスク・注意事項", target: "sd-risk" },
  { label: "未承認医薬品等について", target: "sd-unapproved" },
  { label: "料金", target: "sd-price" },
  { label: "Q&A", target: "sd-faq" },
];

const recommendedFor = [
  "幹細胞培養上清液について知りたい",
  "点滴メニューを検討したい",
  "医師の診察のもとで治療を相談したい",
  "体調や目的に合わせた治療内容を検討したい",
  "再生医療について相談したい",
];

const features = [
  {
    no: "01",
    title: "幹細胞培養上清液 StemSupを用いた点滴メニュー",
    text: "幹細胞を培養する過程で得られる培養上清液を用いた製剤を、点滴で投与するメニューです。",
  },
  {
    no: "02",
    title: "医師の診察のもと治療をご案内",
    text: "医師の診察のもと、治療の適応を確認したうえでご案内します。",
  },
  {
    no: "03",
    title: "体調や目的を確認したうえで治療内容を検討",
    text: "体調や目的を確認し、治療内容を検討します。",
  },
  {
    no: "04",
    title: "幹細胞そのものを投与する治療ではありません",
    text: "本治療で使用するのは、幹細胞そのものではなく、幹細胞を培養する過程で得られる培養上清液を用いた製剤です。",
  },
];

const methodItems = [
  { label: "分類", value: "REGENERATIVE MEDICINE" },
  { label: "施術方法", value: "点滴" },
  { label: "使用製剤", value: "StemSup（幹細胞培養上清液）" },
];

const flowSteps = [
  { no: "01", title: "ご予約", text: "WEB・LINE・電話からご予約ください。" },
  { no: "02", title: "カウンセリング", text: "目的やご希望、体調について確認します。" },
  { no: "03", title: "医師の診察", text: "医師が治療の適応を確認します。" },
  { no: "04", title: "治療内容・料金のご説明", text: "治療内容、料金、リスク、注意事項をご説明します。" },
  { no: "05", title: "点滴", text: "内容に同意いただいたうえで点滴を行います。" },
  { no: "06", title: "アフターケア", text: "施術後の注意事項をご案内します。" },
];

/** 施術時間・麻酔・ダウンタイム・推奨回数・推奨間隔は正式確認前のため「—」を維持する */
const specItems = [
  { label: "施術時間", value: "—" },
  { label: "麻酔", value: "—" },
  { label: "ダウンタイム", value: "—" },
  { label: "推奨回数", value: "—" },
  { label: "推奨間隔", value: "—" },
];

const stemSupPriceRows = [
  { label: "脂肪幹細胞由来培養上清液（StemSup-A）", price: "66,000円" },
  { label: "歯髄幹細胞由来培養上清液（StemSup-DP）", price: "66,000円" },
  { label: "臍帯幹細胞由来培養上清液（StemSup-UC）", price: "110,000円" },
];

const riskItems = [
  "点滴に伴い、穿刺部の痛み、赤み、内出血などが生じる場合があります。",
  "使用製剤に関するリスク・副作用・禁忌については、正式情報を確認のうえ掲載します。",
  "本治療は、幹細胞そのものを投与する治療ではありません。",
];

const faqItems = [
  {
    q: "StemSupは幹細胞そのものですか？",
    a: "いいえ。SBDCで使用するStemSupは、幹細胞そのものではなく、幹細胞を培養する過程で得られる培養上清液を用いた製剤です。",
  },
  {
    q: "お肌管理のStemSupとは違いますか？",
    a: "使用するStemSupを美容医療に活用する点は共通しますが、再生医療カテゴリでは点滴、お肌管理ではプラズマまたはダーマペン4と組み合わせた施術としてご案内しています。",
  },
  {
    q: "何回受けますか？",
    a: "正式な推奨回数は確認中です。診察時に体調や目的を確認したうえでご案内します。",
  },
  {
    q: "施術時間はどのくらいですか？",
    a: "正式な施術時間は確認中です。確定次第掲載します。",
  },
];

const relatedLinks = [
  { label: "再生医療トップ", to: "/treatment/regenerative" },
  { label: "再生医療の料金を見る", to: "/price?tab=regenerative" },
];

/**
 * ページ下部の控えめな関連リンク。
 * 大きなカードUIは使わず、細い罫線・テキストリンク・小さな矢印で構成する。
 */
function RelatedLinks() {
  return (
    <section className="border-t border-background-300/70 bg-background-50 py-16 md:py-20 lg:py-24">
      <div className="u-container">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_1.8fr] md:gap-16 lg:gap-24">
          <Reveal>
            <div className="flex flex-col">
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">RELATED LINKS</span>
              <span className="mt-5 block h-px w-10 bg-accent-500" />
              <h2 className="mt-6 font-heading text-[20px] leading-[1.6] text-foreground-950 md:text-[24px]">
                関連ページ
              </h2>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <ul className="border-t border-foreground-950/12">
              {relatedLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="group flex items-center justify-between gap-4 border-b border-foreground-950/12 py-6 transition-colors hover:text-accent-700"
                  >
                    <span className="font-heading text-[15px] text-foreground-900 transition-colors group-hover:text-accent-700 md:text-base">
                      {link.label}
                    </span>
                    <i
                      className="ri-arrow-right-line text-base text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-accent-700"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function StemSupDripDetail() {
  // 施術時間・麻酔・ダウンタイム等がすべて未確定の場合はセクションごと非表示にする
  const specRows = specItems.filter((item) => !isPendingValue(item.value));
  const visibleIndexItems = indexItems.filter(
    (item) => !(item.target === "sd-downtime" && specRows.length === 0),
  );

  return (
    <>
      <StemSupDripHero />
      <TreatmentIndexNav items={visibleIndexItems} />

      {/* 01 ABOUT */}
      <TreatmentSection id="sd-about" en="01 / ABOUT TREATMENT" jp="StemSup点滴とは" tone="plain">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            <p>
              StemSup点滴は、幹細胞を培養する過程で得られる幹細胞培養上清液 StemSupを、点滴で投与するメニューです。
            </p>
            <p>
              SBDCでは、医師の診察のもと、体調や目的を確認したうえで治療をご案内します。
            </p>
            <p>本治療で使用するのは、幹細胞そのものではありません。</p>
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 02 WHAT IS STEMSUP */}
      <TreatmentSection id="sd-stemsup" en="02 / WHAT IS STEMSUP" jp="StemSupとは" tone="tint">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            <p>StemSupは、幹細胞を培養する過程で得られる培養上清液を用いた製剤です。</p>
            <p>本治療で使用するのは、幹細胞そのものではなく、この培養上清液を用いた製剤です。</p>
            <p>SBDCでは、このStemSupを再生医療カテゴリの点滴メニューとしてご案内しています。</p>
          </div>
          <p className="mt-8 max-w-3xl border-l-2 border-accent-500 pl-4 text-[13px] leading-[2.1] text-foreground-800">
            StemSupは、エクソソーム単体の製剤ではなく、幹細胞培養上清液を用いた製剤です。
          </p>
        </Reveal>
      </TreatmentSection>

      {/* 03 DIFFERENCE */}
      <TreatmentSection
        id="sd-difference"
        en="03 / STEM CELL &amp; CULTURE SUPERNATANT"
        jp="幹細胞そのものとの違い"
        tone="plain"
      >
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            <p>
              この治療で使用するのは、幹細胞そのものではなく、幹細胞を培養した際に得られる培養上清液です。
            </p>
            <p>幹細胞移植や幹細胞そのものを用いる治療とは異なります。</p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2">
            <div className="bg-background-50 p-8 md:p-10">
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">STEM CELL</span>
              <h3 className="mt-4 font-heading text-[18px] text-foreground-950 md:text-xl">幹細胞そのもの</h3>
              <p className="mt-4 text-[13px] leading-[2] text-foreground-700">
                幹細胞移植など、幹細胞そのものを用いる治療があります。
              </p>
            </div>
            <div className="bg-secondary-50 p-8 md:p-10">
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">
                CULTURE SUPERNATANT
              </span>
              <h3 className="mt-4 font-heading text-[18px] text-foreground-950 md:text-xl">幹細胞培養上清液</h3>
              <p className="mt-4 text-[13px] leading-[2] text-foreground-700">
                本治療で使用するのは、幹細胞を培養した際に得られる培養上清液です。
              </p>
            </div>
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 04 RECOMMENDED FOR */}
      <TreatmentSection
        id="sd-concerns"
        en="04 / RECOMMENDED FOR"
        jp="このような方へ"
        lead="以下のようなご相談を承っています。"
        tone="tint"
      >
        <ConcernList items={recommendedFor} />
      </TreatmentSection>

      {/* 05 FEATURES */}
      <TreatmentSection id="sd-features" en="05 / FEATURES" jp="StemSup点滴の特徴" tone="plain">
        <FeatureList items={features} />
      </TreatmentSection>

      {/* 06 METHOD */}
      <TreatmentSection id="sd-method" en="06 / METHOD" jp="施術情報" tone="tint">
        <Reveal>
          <MethodTable items={methodItems} />
        </Reveal>
      </TreatmentSection>

      {/* 07 FLOW */}
      <TreatmentSection id="sd-flow" en="07 / FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={flowSteps} />
      </TreatmentSection>

      {/* 08 DOWNTIME */}
      {/* 08 DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {specRows.length > 0 ? (
        <TreatmentSection id="sd-downtime" en="08 / TIME / DOWNTIME" jp="施術時間・ダウンタイム等" tone="tint">
          <DowntimeTable items={specRows} />
        </TreatmentSection>
      ) : null}

      {/* 09 RISK */}
      <TreatmentSection id="sd-risk" en="09 / RISK / NOTES" jp="リスク・副作用・注意事項" tone="plain">
        <Reveal>
          <RiskList
            items={riskItems}
            note="美容医療は自由診療です。施術前に医師より説明を行います。"
          />
        </Reveal>
      </TreatmentSection>

      {/* 10 UNAPPROVED TREATMENT INFORMATION */}
      <TreatmentSection
        id="sd-unapproved"
        en="10 / IMPORTANT INFORMATION"
        jp="未承認医薬品等について"
        tone="tint"
      >
        <Reveal>
          <div className="max-w-3xl border-l-2 border-accent-500 pl-5 md:pl-6">
            <div className="space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
              <p>
                本治療で使用する幹細胞培養上清液は、医薬品医療機器等法上の承認を受けていない製品です。
              </p>
            </div>
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 11 PRICE */}
      <TreatmentSection id="sd-price" en="11 / PRICE" jp="料金" tone="plain">
        <div className="max-w-3xl">
          <div className="grid grid-cols-[1.6fr_1fr] border-t border-foreground-950/20 pb-3 pt-4 text-[11px] tracking-[0.15em] text-foreground-500">
            <span>対象</span>
            <span className="text-right">料金</span>
          </div>
          {stemSupPriceRows.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[1.6fr_1fr] items-baseline border-t border-foreground-950/12 py-5"
            >
              <span className="pr-6 font-heading text-[15px] text-foreground-950">{row.label}</span>
              <span className="text-right font-label text-[15px] text-foreground-900">{row.price}</span>
            </div>
          ))}
          <p className="mt-4 text-[12px] leading-[2] text-foreground-500">表示価格はすべて税込です。</p>
        </div>
        <div className="mt-8">
          <TreatmentPriceLink categoryId="regenerative" categoryJp="再生医療" />
        </div>
        {/* StemSup点滴 モニターLP導線（医療脱毛と同様、「料金表を見る」の直後に配置。モニター価格は公式サイトに掲載しない） */}
        <div className="mt-6 md:mt-8">
          <FirstVisitMonitorLink
            title="初めての方限定・StemSup点滴モニターのご案内"
            description="初めてご来院いただく方向けのStemSup点滴モニターの内容・条件は、専用ページでご案内しています。"
            label="StemSup点滴モニターを見る"
            url="https://sbscl.biz/saisei/"
          />
        </div>
      </TreatmentSection>

      {/* 12 Q&A */}
      <TreatmentSection id="sd-faq" en="12 / Q&amp;A" jp="よくあるご質問" tone="tint">
        <div className="max-w-3xl">
          <TreatmentFaq items={faqItems} />
        </div>
        <div className="mt-12">
          <Link
            to="/treatment/regenerative"
            className="group inline-flex items-center gap-2 whitespace-nowrap text-[13px] text-foreground-800 transition-colors hover:text-accent-700"
          >
            <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-0.5" aria-hidden />
            再生医療へ戻る
          </Link>
        </div>
      </TreatmentSection>

      {/* RELATED LINKS */}
      <RelatedLinks />

      {/* FINAL CTA */}
      <TreatmentCta lineLabel="StemSup点滴についてLINEで相談" treatmentJp="StemSup点滴" />
    </>
  );
}